import { getMetadataValueByKey, mapEdgesToNodes } from "../../base/utils";

const BANNER_TYPES = {
  HEADER: {
    title: "header",
    baseSlug: "top-header",
    validSlugsPattern: /^top-header-\d+$/,
  },
  POPUP: {
    title: "popup",
    validSlugs: ["top-image"],
  },
  NUMBERED_IMAGES: {
    TOP: {
      baseSlug: "top-image",
      validSlugsPattern: /^top-image-\d+$/,
    },
    BOTTOM: {
      baseSlug: "bottom-image",
      validSlugsPattern: /^bottom-image-\d+$/,
    },
  },
  SIDE_IMAGES: {
    validSlugs: ["right-image", "left-image"],
  },
};

const generateLinkPatterns = (baseSlug, number) => ({
  product: `${baseSlug}-link-${number}`,
  external: `${baseSlug}-external-link-${number}`,
  internal: `${baseSlug}-internal-link-${number}`,
});

export const PageContentsTransformer = (
  data,
  currentChannel,
  currentCategory = null  
) => {
  if (!data) return null;

  return mapEdgesToNodes(data?.pages?.edges)
    .map((i) => {
      const finalData = i.metadata
        .find((item) => item.key === "channels")
        ?.value.split(",")
        .includes(currentChannel);

      if (finalData && matchesCategory(i.metadata, currentCategory)) { 
        return PageContentItemTransformer(i);
      }
    })
    .filter(Boolean);
};

const matchesCategory = (metadata, currentCategory) => {
  const category = getMetadataValueByKey(metadata, "category");
  if (!category) return true;             
  if (!currentCategory) return false;     

  return category
    .split(",")
    .map((c) => c.trim().toLowerCase())
    .includes(String(currentCategory).toLowerCase());
};

const PageContentItemTransformer = ({
  title,
  attributes,
  translation,
  metadata = [],
}) => {
  const normalizedTitle = title.toLowerCase();
  const banners = transformBanners(normalizedTitle, attributes);
  const productSliders = extractProductSliders(attributes);
  const links = attributes.filter((attr) =>
    attr.attribute.slug.includes("link")
  );
  const priority = getMetadataValueByKey(metadata, "priority");

  return {
    priority,
    title: normalizedTitle,
    banners: processBanners(banners, links),
    productSliders: productSliders?.values.map(mapProductSlider) ?? [],
    translation: translation?.title,
    titleInternalLink: findExternalLinkValue(links, "title-internal-link"),
    titleExternalLink: findExternalLinkValue(links, "title-external-link"),
  };
};

const isNumberedImageBanner = (slug) => {
  return (
    BANNER_TYPES.NUMBERED_IMAGES.TOP.validSlugsPattern.test(slug) ||
    BANNER_TYPES.NUMBERED_IMAGES.BOTTOM.validSlugsPattern.test(slug)
  );
};

const transformBanners = (title, attributes) => {
  return attributes
    .filter((attr) => isBannerAttribute(title, attr))
    .map(PageContentBanner);
};

const isBannerAttribute = (title, { attribute }) => {
  if (title === BANNER_TYPES.HEADER.title) {
    return BANNER_TYPES.HEADER.validSlugsPattern.test(attribute.slug);
  }
  if (title === BANNER_TYPES.POPUP.title) {
    return BANNER_TYPES.POPUP.validSlugs.includes(attribute.slug);
  }

  return (
    isNumberedImageBanner(attribute.slug) ||
    BANNER_TYPES.SIDE_IMAGES.validSlugs.includes(attribute.slug)
  );
};

const extractProductSliders = (attributes) => {
  return attributes.find(
    (attr) =>
      attr.attribute.slug === "products-slider" &&
      !attr.attribute.slug.includes("link")
  );
};

const mapProductSlider = (product) => ({
  id: product?.reference,
  name: product?.name,
});

const processBanners = (banners = [], links = []) => {
  return banners.reduce((result, banner) => {
    if (!banner.images.length) return result;

    const processor = getBannerProcessor(banner.name);
    return processor(banner, links, result);
  }, []);
};

const getBannerProcessor = (bannerName) => {
  if (bannerName.startsWith("top-header-")) {
    return processHeaderBanner;
  }
  if (BANNER_TYPES.SIDE_IMAGES.validSlugs.includes(bannerName)) {
    return processSideBanner;
  }
  if (isNumberedImageBanner(bannerName)) {
    return processNumberedImageBanner;
  }
  return processDefaultBanner;
};

// Helper function to find external link value
const findExternalLinkValue = (links, externalSlug) => {
  const externalLinkObj = links.find(
    (link) => link.attribute.slug === externalSlug
  );
  return externalLinkObj?.values?.[0]?.name || "";
};

const processHeaderBanner = (banner, links, result) => {
  const headerNumber = banner.name.split("-").pop();
  const linkPatterns = generateLinkPatterns(
    BANNER_TYPES.HEADER.baseSlug,
    headerNumber
  );

  const images = banner.images.map((image) => ({
    url: image.url,
    reference_product: findLink(links, linkPatterns.product),
    external_url: findExternalLinkValue(links, linkPatterns.external),
    internal_url: findExternalLinkValue(links, linkPatterns.internal),
  }));

  // Find existing group or create new one
  const groupName = "headers";
  let group = result.find((item) => item.name === groupName);

  if (!group) {
    group = {
      name: groupName,
      sections: [],
    };
    result.push(group);
  }

  group.sections.push({
    order: parseInt(headerNumber, 10),
    images,
  });

  // Sort sections by order
  group.sections.sort((a, b) => a.order - b.order);

  return result;
};

const processNumberedImageBanner = (banner, links, result) => {
  const [baseType, , number] = banner.name.split("-"); // e.g., ["top", "image", "1"]
  const baseSlug = `${baseType}-image`;
  const linkPattern = generateLinkPatterns(baseSlug, number);

  const images = banner.images.map((image) => ({
    url: image.url,
    reference_product: findLink(links, linkPattern.product),
    external_url: findExternalLinkValue(links, linkPattern.external),
    internal_url: findExternalLinkValue(links, linkPattern.internal),
  }));

  // Find existing group or create new one
  const groupName = `${baseType}-images`;
  let group = result.find((item) => item.name === groupName);

  if (!group) {
    group = {
      name: groupName,
      sections: [],
    };
    result.push(group);
  }

  group.sections.push({
    order: parseInt(number, 10),
    images,
  });

  // Sort sections by order
  group.sections.sort((a, b) => a.order - b.order);

  return result;
};

const processSideBanner = (banner, links, result) => {
  const linkType = `${banner.name}-link`;
  const externalLinkType = `${banner.name}-external-link`;
  const internalLinkType = `${banner.name}-internal-link`;

  const images = banner.images.map((image) => ({
    url: image.url,
    reference_product: findLink(links, linkType),
    external_url: findExternalLinkValue(links, externalLinkType),
    internal_url: findExternalLinkValue(links, internalLinkType),
  }));

  const existingMerged = result.find((item) => item.name === "merged-image");
  if (existingMerged) {
    existingMerged.images.push(...images);
  } else {
    result.push({ name: "merged-image", images });
  }
  return result;
};

const processDefaultBanner = (banner, links, result) => {
  // For popup banners
  if (banner.name === "top-image") {
    const popupLink = findLink(links, "popup-link-1");
    const externalUrl = findExternalLinkValue(links, "popup-external-link-1");
    const internalUrl = findExternalLinkValue(links, "popup-internal-link-1");

    const images = banner.images.map((image) => ({
      url: image.url,
      reference_product: popupLink,
      external_url: externalUrl,
      internal_url: internalUrl,
    }));

    result.push({ name: banner.name, images });
  } else {
    const baseName = banner.name.split("-")[0];
    const productLink = findLink(links, baseName);
    const externalUrl = findExternalLinkValue(
      links,
      `${baseName}-external-link`
    );

    const images = banner.images.map((image) => ({
      url: image.url,
      reference_product: productLink,
      external_url: externalUrl,
    }));

    result.push({ name: banner.name, images });
  }

  return result;
};

const findLink = (links, slugPart) =>
  links.find((link) => link.attribute.slug.includes(slugPart)) ?? "";

const PageContentBanner = ({ attribute, values }) => {
  if (!attribute || !values) return null;

  return {
    name: attribute?.slug,
    images: values?.map((value) => ({ url: value?.file?.url })) ?? [],
  };
};
